import { useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { products } from "@/mocks/products";
import { useCart } from "@/contexts/CartContext";

// Image labels that describe what each photo shows
const IMAGE_LABELS = [
  "Main View",
  "Lifestyle",
  "Detail",
  "In Use",
  "Craftsmanship",
  "Setting",
];

export default function ProductDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const product = products.find(p => p.id === id);

  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [added, setAdded] = useState(false);
  const [activeTab, setActiveTab] = useState<"description" | "specs" | "shipping">("description");
  const [lightboxOpen, setLightboxOpen] = useState(false);

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-400 mb-4">Product not found.</p>
          <Link to="/products" className="text-[#2D5016] hover:underline cursor-pointer">Back to Products</Link>
        </div>
      </div>
    );
  }

  const related = products.filter(p => p.category === product.category && p.id !== product.id).slice(0, 4);

  const handleAddToCart = () => {
    addToCart(product, quantity);
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  };

  const handleBuyNow = () => {
    addToCart(product, quantity);
    navigate("/cart");
  };

  const handlePrevImage = () => {
    setSelectedImage(prev => (prev === 0 ? product.images.length - 1 : prev - 1));
  };

  const handleNextImage = () => {
    setSelectedImage(prev => (prev === product.images.length - 1 ? 0 : prev + 1));
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main className="pt-[72px]">
        {/* Breadcrumb */}
        <div className="max-w-7xl mx-auto px-6 py-5">
          <div className="flex items-center gap-2 text-sm text-gray-400">
            <Link to="/" className="hover:text-[#2D5016] transition-colors cursor-pointer">Home</Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link to="/products" className="hover:text-[#2D5016] transition-colors cursor-pointer">Products</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-[#1a1a1a] truncate max-w-[200px]">{product.name}</span>
          </div>
        </div>

        {/* Product Section */}
        <div className="max-w-7xl mx-auto px-6 pb-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">

            {/* ── Image Gallery ── */}
            <div className="flex flex-col gap-4">

              {/* Main Image */}
              <div className="relative w-full h-[480px] md:h-[560px] rounded-2xl overflow-hidden bg-[#F9F7F3] group cursor-zoom-in"
                onClick={() => setLightboxOpen(true)}>
                <img
                  src={product.images[selectedImage]}
                  alt={`${product.name} - ${IMAGE_LABELS[selectedImage] ?? ""}`}
                  className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                />

                {/* Image counter badge */}
                <div className="absolute top-4 left-4 bg-black/50 text-white text-xs px-3 py-1.5 rounded-full backdrop-blur-sm flex items-center gap-1.5">
                  <span className="w-3 h-3 flex items-center justify-center"><i className="ri-image-line text-xs"></i></span>
                  {selectedImage + 1} / {product.images.length}
                </div>

                {/* Label badge */}
                <div className="absolute top-4 right-4 bg-white/90 text-[#2D5016] text-xs font-semibold px-3 py-1.5 rounded-full backdrop-blur-sm">
                  {IMAGE_LABELS[selectedImage] ?? ""}
                </div>

                {/* Zoom hint */}
                <div className="absolute bottom-4 right-4 bg-black/40 text-white text-xs px-2.5 py-1.5 rounded-full backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
                  <span className="w-3 h-3 flex items-center justify-center"><i className="ri-zoom-in-line text-xs"></i></span>
                  Click to enlarge
                </div>

                {/* Prev / Next arrows */}
                <button
                  onClick={(e) => { e.stopPropagation(); handlePrevImage(); }}
                  className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 flex items-center justify-center bg-white/80 hover:bg-white rounded-full transition-all opacity-0 group-hover:opacity-100 cursor-pointer"
                >
                  <i className="ri-arrow-left-s-line text-[#1a1a1a] text-lg"></i>
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); handleNextImage(); }}
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 flex items-center justify-center bg-white/80 hover:bg-white rounded-full transition-all opacity-0 group-hover:opacity-100 cursor-pointer"
                >
                  <i className="ri-arrow-right-s-line text-[#1a1a1a] text-lg"></i>
                </button>
              </div>

              {/* Thumbnail Strip — 6 images */}
              <div className="grid grid-cols-6 gap-2">
                {product.images.map((img, i) => (
                  <button
                    key={i}
                    onClick={() => setSelectedImage(i)}
                    className={`relative w-full aspect-square rounded-xl overflow-hidden border-2 transition-all cursor-pointer flex-shrink-0 group ${
                      selectedImage === i
                        ? "border-[#2D5016] ring-1 ring-[#2D5016]/30"
                        : "border-transparent hover:border-gray-300"
                    }`}
                    title={IMAGE_LABELS[i] ?? ""}
                  >
                    <img src={img} alt={IMAGE_LABELS[i] ?? ""} className="w-full h-full object-cover object-top" />
                    {selectedImage !== i && (
                      <div className="absolute inset-0 bg-white/20 group-hover:bg-transparent transition-colors" />
                    )}
                    {/* Label on hover */}
                    <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[9px] text-center py-0.5 opacity-0 group-hover:opacity-100 transition-opacity truncate px-0.5">
                      {IMAGE_LABELS[i]}
                    </div>
                  </button>
                ))}
              </div>

              {/* Brand Trust Strip */}
              <div className="grid grid-cols-3 gap-3 mt-1">
                {[
                  { icon: "ri-leaf-line", title: "Sustainably Sourced", desc: "FSC-certified materials" },
                  { icon: "ri-shield-check-line", title: "5-Year Warranty", desc: "ARAVELLA quality promise" },
                  { icon: "ri-truck-line", title: "White Glove Delivery", desc: "Assembly included" },
                ].map(item => (
                  <div key={item.title} className="flex flex-col items-center text-center bg-[#F9F7F3] rounded-xl p-3 gap-1.5">
                    <span className="w-8 h-8 flex items-center justify-center bg-[#2D5016]/10 rounded-full">
                      <i className={`${item.icon} text-[#2D5016] text-sm`}></i>
                    </span>
                    <span className="text-xs font-semibold text-[#1a1a1a] leading-tight">{item.title}</span>
                    <span className="text-[10px] text-gray-400 leading-tight">{item.desc}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Info */}
            <div>
              <span className="text-[#4A7C2F] text-xs font-medium uppercase tracking-widest">{product.category}</span>
              <h1 className="text-3xl font-bold text-[#1a1a1a] mt-2 mb-3 leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
                {product.name}
              </h1>

              {/* Rating */}
              <div className="flex items-center gap-3 mb-5">
                <div className="flex items-center gap-0.5">
                  {[1,2,3,4,5].map(s => (
                    <i key={s} className={`text-base ${s <= Math.round(product.rating) ? "ri-star-fill text-amber-400" : "ri-star-line text-gray-200"}`}></i>
                  ))}
                </div>
                <span className="text-gray-500 text-sm">{product.rating} ({product.reviewCount} reviews)</span>
                <a href="#reviews" className="text-[#2D5016] text-sm hover:underline cursor-pointer">Read reviews</a>
              </div>

              {/* Price */}
              <div className="flex items-baseline gap-3 mb-6">
                <span className="text-4xl font-bold text-[#1a1a1a]">${product.price}</span>
                {product.originalPrice && (
                  <span className="text-gray-400 text-lg line-through">${product.originalPrice}</span>
                )}
                {product.originalPrice && (
                  <span className="bg-red-50 text-red-500 text-sm font-semibold px-2 py-0.5 rounded">
                    Save ${product.originalPrice - product.price}
                  </span>
                )}
              </div>

              <p className="text-gray-500 text-sm leading-relaxed mb-6">{product.description}</p>

              {/* Features */}
              <div className="mb-6">
                <h4 className="text-sm font-semibold text-[#1a1a1a] mb-3">Key Features</h4>
                <ul className="space-y-2">
                  {product.features.map(f => (
                    <li key={f} className="flex items-center gap-2 text-sm text-gray-500">
                      <i className="ri-check-line text-[#2D5016] flex-shrink-0"></i>
                      {f}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Quantity */}
              <div className="mb-6">
                <h4 className="text-sm font-semibold text-[#1a1a1a] mb-3">Quantity</h4>
                <div className="flex items-center gap-3">
                  <div className="flex items-center border border-gray-200 rounded-lg overflow-hidden">
                    <button
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      className="w-10 h-10 flex items-center justify-center hover:bg-gray-50 transition-colors cursor-pointer text-gray-500"
                    >
                      <i className="ri-subtract-line"></i>
                    </button>
                    <span className="w-12 text-center text-sm font-semibold text-[#1a1a1a]">{quantity}</span>
                    <button
                      onClick={() => setQuantity(quantity + 1)}
                      className="w-10 h-10 flex items-center justify-center hover:bg-gray-50 transition-colors cursor-pointer text-gray-500"
                    >
                      <i className="ri-add-line"></i>
                    </button>
                  </div>
                  <span className="text-gray-400 text-sm">In Stock</span>
                </div>
              </div>

              {/* Buttons */}
              <div className="flex flex-col gap-3 mb-8">
                <button
                  onClick={handleAddToCart}
                  className={`w-full h-14 rounded-full font-semibold text-sm tracking-wide transition-all duration-300 cursor-pointer ${
                    added
                      ? "bg-[#2D5016] text-white"
                      : "bg-[#EEF5E8] text-[#2D5016] hover:bg-[#2D5016] hover:text-white"
                  }`}
                >
                  {added ? "Added to Cart!" : "Add to Cart"}
                </button>
                <button
                  onClick={handleBuyNow}
                  className="w-full h-14 bg-[#1a1a1a] hover:bg-[#333] text-white rounded-full font-semibold text-sm tracking-wide transition-all duration-300 cursor-pointer"
                >
                  Buy Now
                </button>
              </div>

              {/* Trust */}
              <div className="flex flex-wrap gap-4 text-xs text-gray-400">
                <span className="flex items-center gap-1.5"><i className="ri-truck-line text-[#2D5016]"></i> Free shipping over $500</span>
                <span className="flex items-center gap-1.5"><i className="ri-shield-check-line text-[#2D5016]"></i> 5-year warranty</span>
                <span className="flex items-center gap-1.5"><i className="ri-refresh-line text-[#2D5016]"></i> 30-day returns</span>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="mt-16">
            <div className="flex gap-0 border-b border-gray-100 mb-8">
              {(["description", "specs", "shipping"] as const).map(tab => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-6 py-3 text-sm font-medium capitalize transition-all cursor-pointer whitespace-nowrap ${
                    activeTab === tab
                      ? "border-b-2 border-[#2D5016] text-[#2D5016]"
                      : "text-gray-400 hover:text-gray-600"
                  }`}
                >
                  {tab === "specs" ? "Specifications" : tab === "shipping" ? "Shipping Info" : "Description"}
                </button>
              ))}
            </div>
            <div className="bg-[#F9F7F3] rounded-2xl p-8">
              {activeTab === "description" && (
                <div>
                  <p className="text-gray-600 leading-relaxed mb-4">{product.description}</p>
                  <ul className="space-y-2">
                    {product.features.map(f => (
                      <li key={f} className="flex items-center gap-2 text-sm text-gray-500">
                        <i className="ri-checkbox-circle-line text-[#2D5016]"></i> {f}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {activeTab === "specs" && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {[
                    { label: "Material", value: product.material },
                    { label: "Dimensions", value: product.dimensions },
                    { label: "Weight", value: product.weight },
                    { label: "Category", value: product.category },
                  ].map(spec => (
                    <div key={spec.label} className="bg-white rounded-xl p-4">
                      <div className="text-xs text-gray-400 uppercase tracking-wide mb-1">{spec.label}</div>
                      <div className="text-sm font-semibold text-[#1a1a1a]">{spec.value}</div>
                    </div>
                  ))}
                </div>
              )}
              {activeTab === "shipping" && (
                <div className="space-y-4 text-sm text-gray-600">
                  <p><strong>Free Shipping</strong> on all orders over $500 within the contiguous US.</p>
                  <p><strong>Standard Delivery:</strong> 5-10 business days</p>
                  <p><strong>White Glove Delivery:</strong> Available for large items — includes assembly and placement.</p>
                  <p><strong>Returns:</strong> 30-day hassle-free returns. Items must be in original condition.</p>
                  <p>Contact us at <a href="mailto:aravellallc@outlook.com" className="text-[#2D5016] hover:underline">aravellallc@outlook.com</a> for any shipping questions.</p>
                </div>
              )}
            </div>
          </div>

          {/* Reviews */}
          <div id="reviews" className="mt-16">
            <h2 className="text-2xl font-bold text-[#1a1a1a] mb-8" style={{ fontFamily: "'Playfair Display', serif" }}>
              Customer Reviews
            </h2>
            <div className="bg-[#F9F7F3] rounded-2xl p-8 mb-8 flex flex-col sm:flex-row items-center gap-8">
              <div className="text-center">
                <div className="text-6xl font-bold text-[#1a1a1a]">{product.rating}</div>
                <div className="flex items-center justify-center gap-0.5 my-2">
                  {[1,2,3,4,5].map(s => (
                    <i key={s} className={`text-lg ${s <= Math.round(product.rating) ? "ri-star-fill text-amber-400" : "ri-star-line text-gray-200"}`}></i>
                  ))}
                </div>
                <div className="text-gray-400 text-sm">{product.reviewCount} reviews</div>
              </div>
              <div className="flex-1 w-full">
                {[5,4,3,2,1].map(star => {
                  const count = product.reviews.filter(r => r.rating === star).length;
                  const pct = product.reviews.length > 0 ? (count / product.reviews.length) * 100 : 0;
                  return (
                    <div key={star} className="flex items-center gap-3 mb-2">
                      <span className="text-xs text-gray-400 w-4">{star}</span>
                      <i className="ri-star-fill text-amber-400 text-xs"></i>
                      <div className="flex-1 bg-gray-200 rounded-full h-2">
                        <div className="bg-amber-400 h-2 rounded-full transition-all" style={{ width: `${pct}%` }}></div>
                      </div>
                      <span className="text-xs text-gray-400 w-4">{count}</span>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="space-y-5">
              {product.reviews.map(review => (
                <div key={review.id} className="bg-white border border-gray-100 rounded-2xl p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0">
                      <img src={review.avatar} alt={review.author} className="w-full h-full object-cover" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <div>
                          <span className="font-semibold text-[#1a1a1a] text-sm">{review.author}</span>
                          <span className="text-gray-400 text-xs ml-2">{review.location}</span>
                        </div>
                        <span className="text-gray-400 text-xs">{review.date}</span>
                      </div>
                      <div className="flex items-center gap-0.5 my-1.5">
                        {[1,2,3,4,5].map(s => (
                          <i key={s} className={`text-xs ${s <= review.rating ? "ri-star-fill text-amber-400" : "ri-star-line text-gray-200"}`}></i>
                        ))}
                      </div>
                      <p className="text-gray-600 text-sm leading-relaxed">{review.comment}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Related Products */}
          {related.length > 0 && (
            <div className="mt-16">
              <h2 className="text-2xl font-bold text-[#1a1a1a] mb-8" style={{ fontFamily: "'Playfair Display', serif" }}>
                You May Also Like
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
                {related.map(p => (
                  <Link key={p.id} to={`/product/${p.id}`} className="group bg-[#F9F7F3] rounded-xl overflow-hidden hover:shadow-md transition-all cursor-pointer">
                    <div className="w-full h-48">
                      <img src={p.images[0]} alt={p.name} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                    </div>
                    <div className="p-4">
                      <h4 className="text-sm font-semibold text-[#1a1a1a] line-clamp-2 mb-1">{p.name}</h4>
                      <span className="text-[#2D5016] font-bold text-sm">${p.price}</span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Lightbox */}
      {lightboxOpen && (
        <div
          className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center"
          onClick={() => setLightboxOpen(false)}
        >
          <button
            className="absolute top-5 right-5 w-10 h-10 flex items-center justify-center bg-white/10 hover:bg-white/20 rounded-full text-white cursor-pointer transition-colors"
            onClick={() => setLightboxOpen(false)}
          >
            <i className="ri-close-line text-xl"></i>
          </button>
          <button
            className="absolute left-5 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center bg-white/10 hover:bg-white/20 rounded-full text-white cursor-pointer transition-colors"
            onClick={(e) => { e.stopPropagation(); handlePrevImage(); }}
          >
            <i className="ri-arrow-left-s-line text-xl"></i>
          </button>
          <div className="max-w-4xl max-h-[85vh] w-full mx-10" onClick={e => e.stopPropagation()}>
            <img
              src={product.images[selectedImage]}
              alt={product.name}
              className="w-full h-full object-contain rounded-xl"
            />
            <div className="flex justify-center gap-2 mt-4">
              {product.images.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setSelectedImage(i)}
                  className={`w-2 h-2 rounded-full transition-all cursor-pointer ${i === selectedImage ? "bg-white w-6" : "bg-white/40"}`}
                />
              ))}
            </div>
            <p className="text-center text-white/60 text-sm mt-2">{IMAGE_LABELS[selectedImage]} — {selectedImage + 1} of {product.images.length}</p>
          </div>
          <button
            className="absolute right-5 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center bg-white/10 hover:bg-white/20 rounded-full text-white cursor-pointer transition-colors"
            onClick={(e) => { e.stopPropagation(); handleNextImage(); }}
          >
            <i className="ri-arrow-right-s-line text-xl"></i>
          </button>
        </div>
      )}

      <Footer />
    </div>
  );
}
